import { useState } from "react";
import { Copy, Check, CheckCircle2, Send, Loader2 } from "lucide-react";
import { api, formatError, PHONE_DISPLAY } from "@/lib/api";
import { Eyebrow, FadeIn } from "@/components/Reveal";
import { WhatsAppIcon } from "@/components/WhatsAppFloat";

const inputCls =
  "w-full rounded-lg border border-gold/20 bg-ink px-4 py-3 text-ivory placeholder:text-stone2/60 outline-none transition-colors focus:border-gold";
const labelCls = "mb-1.5 block text-xs font-semibold uppercase tracking-[0.16em] text-gold-subtle";

const BUSCANDO = ["Alquiler", "Compra", "Opción a compra", "Aún no lo tengo claro"];
const PERSONAS = ["1", "2", "3", "4", "5 o más"];
const MASCOTAS = ["No", "Sí, pequeña", "Sí, mediana/grande"];
const INGRESOS = ["Menos de 1.000 €", "1.000 € – 1.500 €", "1.500 € – 2.500 €", "2.500 € – 3.500 €", "Más de 3.500 €", "Prefiero comentarlo"];
const ASNEF = ["No", "Sí", "No estoy seguro/a", "Prefiero comentarlo"];
const AHORROS = ["Menos de 3.000 €", "3.000 € – 6.000 €", "6.000 € – 12.000 €", "Más de 12.000 €", "Prefiero comentarlo"];

const BuscaTuPiso = () => {
  const [f, setF] = useState({
    name: "", phone: "", buscando: "Alquiler", zonas: "", presupuesto: "",
    personas: "1", mascotas: "No", ingresos: "Prefiero comentarlo",
    asnef: "Prefiero comentarlo", ahorros: "Prefiero comentarlo", notas: "",
  });
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");
  const [sent, setSent] = useState(false);
  const [waUrl, setWaUrl] = useState("");
  const [copied, setCopied] = useState(false);

  const set = (k) => (e) => setF((v) => ({ ...v, [k]: e.target.value }));

  const copyLink = () => {
    navigator.clipboard?.writeText(`${window.location.origin}/busca-tu-piso`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setErr("");
    try {
      const { data } = await api.post("/client-profile", f);
      setWaUrl(data.wa_url);
      setSent(true);
      window.open(data.wa_url, "_blank");
    } catch (error) {
      setErr(formatError(error.response?.data?.detail) || "No se pudo enviar. Escríbenos directamente por WhatsApp.");
    }
    setSending(false);
  };

  if (sent)
    return (
      <main className="mx-auto max-w-2xl px-6 pb-28 pt-32 md:pt-40">
        <div className="rounded-2xl border border-wa/30 bg-wa/10 p-8 text-center" data-testid="ficha-success-panel">
          <CheckCircle2 className="mx-auto text-wa" size={44} />
          <h1 className="mt-4 font-serif text-3xl text-ivory">¡Ficha enviada!</h1>
          <p className="mt-3 text-sm text-stone2">
            Se abrió WhatsApp con tu ficha lista. Si no se abrió, pulsa abajo. Te escribimos muy pronto con opciones que encajan contigo.
          </p>
          <a
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="ficha-reopen-whatsapp-button"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-wa px-6 py-3 font-semibold text-ink transition-transform hover:-translate-y-0.5"
          >
            <WhatsAppIcon className="h-5 w-5" /> Abrir WhatsApp {PHONE_DISPLAY}
          </a>
        </div>
      </main>
    );

  return (
    <main className="mx-auto max-w-3xl px-6 pb-28 pt-32 md:pt-40">
      <FadeIn>
        <Eyebrow>Ficha de cliente</Eyebrow>
        <h1 className="mt-4 font-serif text-4xl uppercase tracking-wide text-ivory md:text-6xl">
          Busca tu <span className="text-gold-metallic">piso</span>
        </h1>
        <p className="mt-5 max-w-xl leading-relaxed text-stone2">
          Cuéntanos qué buscas y tu situación — en 2 minutos. Nosotros te respondemos por WhatsApp con opciones reales
          que encajan contigo, sin compromiso.
        </p>
      </FadeIn>

      <FadeIn delay={0.1}>
        <form onSubmit={submit} className="mt-10 space-y-6 rounded-2xl border border-gold/20 bg-ink2/80 p-6 shadow-[0_10px_30px_rgba(0,0,0,0.8)] md:p-8" data-testid="ficha-form">
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Nombre y apellidos *</label>
              <input required value={f.name} onChange={set("name")} placeholder="María García López" data-testid="ficha-input-name" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Teléfono *</label>
              <input required type="tel" value={f.phone} onChange={set("phone")} placeholder="600 000 000" data-testid="ficha-input-phone" className={inputCls} />
            </div>
          </div>

          <div>
            <label className={labelCls}>¿Qué estás buscando?</label>
            <div className="flex flex-wrap gap-2">
              {BUSCANDO.map((o) => (
                <button type="button" key={o} onClick={() => setF((v) => ({ ...v, buscando: o }))} data-testid={`ficha-chip-buscando-${o.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={`rounded-full px-4 py-2 text-xs font-bold uppercase tracking-[0.12em] transition-all ${f.buscando === o ? "btn-gold-metallic text-ink" : "border border-gold/25 text-gold hover:bg-gold/10"}`}>
                  {o}
                </button>
              ))}
            </div>
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label className={labelCls}>¿En qué zona o zonas te gustaría vivir?</label>
              <input value={f.zonas} onChange={set("zonas")} placeholder="Ej.: Centro, playa, cerca del colegio…" data-testid="ficha-input-zonas" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>¿Qué presupuesto aproximado tienes?</label>
              <input value={f.presupuesto} onChange={set("presupuesto")} placeholder="Ej.: 800 €/mes o hasta 250.000 €" data-testid="ficha-input-presupuesto" className={inputCls} />
            </div>
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label className={labelCls}>¿Cuántas personas vivirían en la vivienda?</label>
              <select value={f.personas} onChange={set("personas")} data-testid="ficha-select-personas" className={inputCls}>
                {PERSONAS.map((o) => <option key={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>¿Tienes mascotas?</label>
              <select value={f.mascotas} onChange={set("mascotas")} data-testid="ficha-select-mascotas" className={inputCls}>
                {MASCOTAS.map((o) => <option key={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>¿Cuánto cobras al mes, aproximadamente?</label>
              <select value={f.ingresos} onChange={set("ingresos")} data-testid="ficha-select-ingresos" className={inputCls}>
                {INGRESOS.map((o) => <option key={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>¿Cuánto tienes ahorrado ahora mismo?</label>
              <select value={f.ahorros} onChange={set("ahorros")} data-testid="ficha-select-ahorros" className={inputCls}>
                {AHORROS.map((o) => <option key={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>¿Apareces en algún fichero de impagos (ASNEF, RAI…)?</label>
              <select value={f.asnef} onChange={set("asnef")} data-testid="ficha-select-asnef" className={inputCls}>
                {ASNEF.map((o) => <option key={o}>{o}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className={labelCls}>¿Algo más que quieras contarnos?</label>
            <textarea rows={3} value={f.notas} onChange={set("notas")} placeholder="Opcional…" data-testid="ficha-input-notas" className={inputCls} />
          </div>

          {err && <p className="text-sm text-red-400" data-testid="ficha-error">{err}</p>}

          <button
            type="submit"
            disabled={sending}
            data-testid="ficha-submit-button"
            className="flex w-full items-center justify-center gap-3 rounded-full bg-wa py-4 text-sm font-bold uppercase tracking-[0.2em] text-ink transition-transform hover:-translate-y-0.5 disabled:opacity-60"
          >
            {sending ? <Loader2 size={16} className="animate-spin" /> : <WhatsAppIcon className="h-5 w-5" />}
            {sending ? "Enviando…" : "Enviar y empezar por WhatsApp"}
          </button>
        </form>
      </FadeIn>

      <FadeIn delay={0.15}>
        <div className="mt-8 rounded-xl border border-gold/20 bg-ink2/70 p-5">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-gold-subtle">Enlace directo de esta ficha</p>
          <p className="mt-2 text-sm text-stone2">Compártelo con quien busque casa — nos llegará todo por WhatsApp:</p>
          <button
            onClick={copyLink}
            data-testid="copy-ficha-link-button"
            className="mt-3 flex w-full items-center gap-2 rounded-lg border border-gold/30 px-4 py-2.5 text-left text-sm text-gold transition-colors hover:bg-gold/10 sm:w-fit"
          >
            {copied ? <Check size={15} className="shrink-0" /> : <Copy size={15} className="shrink-0" />}
            <span className="break-all">{copied ? "¡Enlace copiado!" : `${window.location.origin}/busca-tu-piso`}</span>
          </button>
        </div>
      </FadeIn>
    </main>
  );
};

export default BuscaTuPiso;
